import type { Skill } from '@lmc/ts-common/lib/rota/dtos/rota-patterns'
import { theme } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { Tag, Tooltip } from 'antd'
import { lowerFirst } from 'lodash'
import { useTranslation } from 'react-i18next'
import useUserSkills from 'screens/Rostering/hooks/useUserSkills'
import { WarningIcon } from 'screens/Rostering/RotaBuilder/common/components/icons'
import styled from 'styled-components'

type SkillsToMatchContainerProps = {
  skillsIds: (Skill & { areMatched?: boolean })[]
  withColor?: boolean
}

type MatchSkill = {
  name: string
  numberRequired: number
  areMatched: boolean
}

const SkillsToMatchContainer = ({
  skillsIds,
  withColor
}: SkillsToMatchContainerProps) => {
  const { t } = useTranslation()

  const { allSkills } = useUserSkills()

  if (!skillsIds?.length) {
    return <StyledLightText>{t('NO_SKILLS_SELECTED')}</StyledLightText>
  }

  const skills: MatchSkill[] = skillsIds
    .map((skillId) => {
      const skill = allSkills.find((skill) => skill.id === skillId?.skillId)
      return skill
        ? {
            name: skill.name,
            numberRequired: skillId.skillSupplyNumber,
            areMatched: skillId?.areMatched ?? false
          }
        : null
    })
    .filter((skill): skill is MatchSkill => skill !== null)

  return (
    <StyledSkillsContainer>
      {skills?.map((skill) => {
        const color = skill.areMatched === false ? 'orange' : 'green'

        return (
          <Tooltip
            placement="bottom"
            color={theme.color.neutral.color3}
            title={
              <ContentContainer>
                <Text>
                  {`${skill.numberRequired} ${
                    skill.numberRequired > 1
                      ? lowerFirst(t('TEAM_MEMBERS_REQUIRED'))
                      : lowerFirst(t('TEAM_MEMBER_REQUIRED'))
                  }`}
                </Text>
              </ContentContainer>
            }
          >
            <StyledTag
              color={withColor ? color : undefined}
              icon={withColor && !skill.areMatched && <WarningIcon />}
            >
              <Text weight="medium" level={2}>
                {skill.name}
              </Text>
              {skill.numberRequired > 1 && (
                <Text weight="medium" level={2}>
                  {`x${skill.numberRequired}`}
                </Text>
              )}
            </StyledTag>
          </Tooltip>
        )
      })}
    </StyledSkillsContainer>
  )
}

const StyledTag = styled(Tag)`
  padding: 4px 8px;
  margin: 0;
  color: ${({ theme }) => theme.color.neutral.color3};
  overflow: hidden;
  max-width: 100%;
  display: flex;
  gap: 4px;
  white-space: wrap;

  &:hover {
    cursor: pointer;
  }
`

const StyledSkillsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
`
const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 4px 8px;
  color: ${(props) => props.theme.color.neutral.color11};
`
const StyledLightText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
  white-space: nowrap;
`

export default SkillsToMatchContainer
